import { useCallback } from 'react';

import { useEmptyChatLaunchSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

export interface CombinedProviderAndCustomActionsResult {
	mergedGetActions: () => Promise<ConversationAction[]>;
	/**
	 * When true, the merged getActions result should fully replace all other action sources
	 * (dynamic starters, prompt-of-day, etc). Derived from the store's replaceProviderActions flag.
	 * Pass this directly to <ActionsProvider prioritizeCustomActionResults>.
	 */
	replaceProviderActions: boolean;
	/**
	 * When replaceProviderActions is true, set to the number of custom actions so all are shown.
	 * When false, undefined (lets <ActionsProvider> use its own default of 3).
	 */
	numStartersToShow: number | undefined;
}

/**
 * Merges prop-based and runtime (store) action sources with the following precedence:
 *
 * 1. Runtime store actions (setEmptyChatLaunch) — highest priority.
 *    When present with replaceProviderActions=true, completely replaces the prop getActions.
 * 2. Prop-based getActions — used when no runtime store actions are present.
 *
 * Note: prioritizeCustomActionResults (the prop on ConversationAssistant) only controls
 * how actions are displayed vs dynamic starters — it does NOT affect which action source wins.
 * The store always wins over the prop when replaceProviderActions=true.
 */
export function useCombinedProviderAndCustomActions(
	getActions: (() => Promise<ConversationAction[]>) | undefined,
): CombinedProviderAndCustomActionsResult {
	const [{ customConversationActions, replaceProviderActions }] = useEmptyChatLaunchSelector();

	// Recreated whenever customConversationActions, replaceProviderActions, or getActions changes.
	// Reading values directly (no ref) ensures the callback always has fresh data.
	const mergedGetActions = useCallback(async () => {
		const runtimeActions = customConversationActions as ConversationAction[] | undefined;

		// Precedence rule: runtime store actions (setEmptyChatLaunch) take priority over prop getActions.
		if (runtimeActions?.length && replaceProviderActions) {
			// Runtime launch with replace=true: store actions completely replace the prop provider.
			// getActions is intentionally NOT called here — store actions win entirely.
			// Note: prioritizeCustomActionResults (prop) is still applied downstream to these actions,
			// but it doesn't affect WHICH actions are shown — only that dynamic starters are suppressed.
			return runtimeActions;
		}

		// For all other cases we need the provider actions.
		const providerActions = (await getActions?.()) ?? [];

		if (runtimeActions?.length) {
			// Runtime launch without replace: prepend store actions to prop provider actions.
			return [...runtimeActions, ...providerActions];
		}

		// No runtime actions: fall through to prop provider only.
		return providerActions;
	}, [getActions, customConversationActions, replaceProviderActions]);

	return {
		mergedGetActions,
		replaceProviderActions: Boolean(replaceProviderActions),
		// When replacing all provider actions, ensure all custom actions are shown rather than being
		// sliced to the default display limit of 3.
		numStartersToShow:
			replaceProviderActions && customConversationActions && customConversationActions.length > 0
				? customConversationActions?.length
				: undefined,
	};
}
